import { useMemo, useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Users, ShieldCheck, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api, ApiCallError } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/ui/data-table';
import { AppClientFormDialog, type ClientRow } from '@/components/app-client-form-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const DOC_LABEL: Record<string, string> = { ruc: 'RUC', ci: 'CI', sin: 'Sin doc.' };
const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiCallError ? (e.body.message ?? e.body.error) : fallback;

export function AppClientesPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editClient, setEditClient] = useState<ClientRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<ClientRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['app-clients'],
    queryFn: () => api<{ items: ClientRow[] }>('/api/app/clients'),
    enabled: !!me,
  });

  const del = useMutation({
    mutationFn: (id: string) => api(`/api/app/clients/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Cliente eliminado');
      qc.invalidateQueries({ queryKey: ['app-clients'] });
    },
    onError: (e) => toast.error(errMsg(e, 'No se pudo eliminar')),
  });

  const items = data?.items ?? [];

  const columns = useMemo<ColumnDef<ClientRow, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nombre',
        cell: ({ row }) => (
          <div>
            <div className="font-medium leading-tight">{row.original.name}</div>
            {row.original.razonSocial && row.original.razonSocial !== row.original.name && (
              <div className="text-xs text-muted-foreground">{row.original.razonSocial}</div>
            )}
          </div>
        ),
      },
      {
        id: 'documento',
        header: 'Documento',
        accessorFn: (c) => c.ruc ?? '',
        cell: ({ row }) =>
          row.original.ruc ? (
            <span className="inline-flex items-center gap-1.5">
              <Badge variant="secondary">{DOC_LABEL[row.original.docType] ?? row.original.docType}</Badge>
              <span className="font-mono text-xs">{row.original.ruc}</span>
              {row.original.docType === 'ruc' && row.original.razonSocial && (
                <ShieldCheck className="size-3.5 text-success" />
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => row.original.email || <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: 'phone',
        header: 'Teléfono',
        cell: ({ row }) => row.original.phone || <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'acciones',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" title="Acciones">
                  <MoreVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setEditClient(row.original);
                    setFormOpen(true);
                  }}
                >
                  <Pencil /> Editar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={() => setDeleteRow(row.original)}>
                  <Trash2 /> Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    []
  );

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-sm text-muted-foreground">
            A quién le facturás o cotizás. El RUC se valida contra la SET.
          </p>
        </div>
        <Button
          className="w-full sm:w-auto"
          onClick={() => {
            setEditClient(null);
            setFormOpen(true);
          }}
        >
          <Plus className="mr-2 size-4" /> Nuevo cliente
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-12 text-center text-muted-foreground">
          <Users className="mx-auto mb-2 size-6" />
          Todavía no cargaste clientes. Creá uno o agregalo al vuelo al facturar.
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          pageSize={15}
          searchPlaceholder="Buscar por nombre o RUC…"
          exportFileName="clientes"
          exportRows={(rows) =>
            rows.map((c) => ({
              Nombre: c.name,
              'Razón social': c.razonSocial ?? '',
              Documento: DOC_LABEL[c.docType] ?? c.docType,
              'Nº': c.ruc ?? '',
              Email: c.email ?? '',
              Teléfono: c.phone ?? '',
              Dirección: c.address ?? '',
            }))
          }
          onRowClick={(c) => {
            setEditClient(c);
            setFormOpen(true);
          }}
        />
      )}

      <AppClientFormDialog open={formOpen} onOpenChange={setFormOpen} initial={editClient} />

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow?.name}. Las facturas/cotizaciones ya emitidas conservan sus datos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteRow) del.mutate(deleteRow.id);
                setDeleteRow(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
